import type { ZodType } from "zod";
import z from "zod";

/**
 * What an instrument version is called: a path, from the general to the
 * particular — `["cans", "all_ages", "2.1", "r3"]`.
 *
 * A path rather than a set of fields, because the parts mean different things
 * to different instruments and a scheme that fixed them would have to be
 * widened by migration. Two properties of the path are load-bearing and the
 * rest is opaque:
 *
 * - the **first segment is the family**, and is the namespace item keys live
 *   in: two variants of one instrument share it, so a key means the same
 *   question in both;
 * - a **prefix names a set of versions**, which is what configuration selects.
 *   A body is entitled to `cans/all_ages/2.1`, and what it starts is the newest
 *   published name extending that.
 *
 * Nothing reads a segment's digits. Whether `2.1` follows `2.0` is the
 * instrument owner's convention, and a scheme that gained a segment would not
 * change anything here.
 */
export type InstrumentNameUnbranded = readonly [string, ...string[]];

/**
 * A segment: the spelling a path renders with, so a name round-trips through a
 * string.
 *
 * Wider than what may be published (see `nameSpellingIssues`), because this
 * also reads names back, and a reader must accept whatever was ever stored.
 */
const segment = z
  .string()
  .regex(/^[a-z0-9]+(?:[-._][a-z0-9]+)*$/, "instrument.name.segmentInvalid");

export const InstrumentNameSchema = z
  .tuple([segment], segment)
  .brand<"haven-common:InstrumentName">() satisfies ZodType<InstrumentNameUnbranded>;

export type InstrumentName = z.infer<typeof InstrumentNameSchema>;

/** The separator a name renders with, and the one a segment may not contain. */
const SEPARATOR = "/";

/**
 * A name as one string: what a URL, a setting and a log line carry.
 *
 * Takes any list of segments rather than a whole name, so a row read back from
 * the database — where the column is a plain array — renders without being
 * parsed first. Parsing is for the paths a decision is made on.
 */
export const renderInstrumentName = (name: readonly string[]): string =>
  name.join(SEPARATOR);

/** Reads a rendered name back. Refuses anything a segment may not be. */
export const parseInstrumentName = (rendered: string) =>
  InstrumentNameSchema.safeParse(rendered.split(SEPARATOR));

/**
 * Whether a name is at or below a prefix — `cans/all_ages/2.1/r3` is within
 * `cans/all_ages/2.1`, and a name is within itself.
 *
 * Segment by segment rather than by string prefix, so `cans/all_ages/2.10` is
 * not taken to be within `cans/all_ages/2.1`.
 */
export const isWithinPrefix = (
  name: InstrumentNameUnbranded,
  prefix: InstrumentNameUnbranded,
): boolean =>
  prefix.length <= name.length &&
  prefix.every((part, index) => name[index] === part);

/** The family a name belongs to: its first segment, and the namespace its item keys live in. */
export const familyOf = (name: InstrumentNameUnbranded): string => name[0];

/**
 * The name one segment shorter, or nothing where there is only one segment.
 *
 * What a body is entitled to is a published name without its last segment, and
 * this is that operation named once rather than spelt out wherever a prefix is
 * wanted.
 */
export const parentOf = (
  name: InstrumentNameUnbranded,
): InstrumentNameUnbranded | undefined =>
  name.length === 1 ? undefined : [name[0], ...name.slice(1, -1)];
